const STORAGE_KEY = 'next-step-redirect-tracker';
const REDIRECT_LIMIT = 8;
const LOOP_WINDOW_MS = 5_000;

interface RedirectTracker {
  count: number;
  firstRedirectAt: number;
  destination: string;
}

const isRedirectTracker = (value: unknown): value is RedirectTracker => {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.count === 'number' &&
    typeof record.firstRedirectAt === 'number' &&
    typeof record.destination === 'string'
  );
};

const readTracker = (raw: string | null): RedirectTracker | null => {
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    return isRedirectTracker(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export interface RedirectCircuitBreakerResult {
  tripped: boolean;
  count: number;
}

// Fails open: a storage exception must never block the redirect, since this runs
// inside next-step's load() promise where a throw escalates to a full-page error.
export function shouldBreakRedirectLoop(destination: string): RedirectCircuitBreakerResult {
  try {
    const now = Date.now();
    const tracker = readTracker(sessionStorage.getItem(STORAGE_KEY));

    // Only a machine-driven loop can hit the limit inside the window. A customer
    // moving through a multi-view step also re-targets one destination (the step
    // stays incomplete until its last save), but each of those costs a round-trip
    // and human think-time, so the burst expires long before the count builds.
    const continuesBurst =
      tracker !== null &&
      tracker.destination === destination &&
      now - tracker.firstRedirectAt <= LOOP_WINDOW_MS;

    const count = continuesBurst ? tracker.count + 1 : 1;
    const firstRedirectAt = continuesBurst ? tracker.firstRedirectAt : now;

    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ count, firstRedirectAt, destination }));
    return { tripped: count > REDIRECT_LIMIT, count };
  } catch {
    return { tripped: false, count: 0 };
  }
}
